import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Text,
  Badge,
  useToast,
  useColorModeValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Select,
  Textarea,
  Progress,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Tooltip,
  Flex,
  Icon,
  Divider,
  Spinner,
  Center
} from '@chakra-ui/react';
import {
  FaSearch,
  FaUpload,
  FaDownload,
  FaEye,
  FaTrash,
  FaHistory,
  FaFolder,
  FaFilter,
  FaPlus,
  FaFileAlt,
  FaFilePdf,
  FaFileWord,
  FaEllipsisV,
  FaTimes
} from 'react-icons/fa';
import { HiOutlineDocumentText } from 'react-icons/hi';
import axios from 'axios';
import CategoryManager from './CategoryManager';
import DocumentPreview from './DocumentPreview';
import VersionHistory from './VersionHistory';

const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_VERCEL_API_URL || 'https://api.veridicus.com.br'
  : process.env.REACT_APP_LOCAL_API_URL || 'http://localhost:5000';

const Documentos = ({ clienteId }) => {
  const [documents, setDocuments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showVersionModal, setShowVersionModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  
  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    file: null,
    category: '',
    description: ''
  });

  const toast = useToast();
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const bgColor = useColorModeValue('white', 'gray.800');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const userType = localStorage.getItem('userType') || '';
  const canModify = ['master', 'diretoria', 'associado'].includes(userType);

  // Fetch documents and categories
  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = {};
      
      if (selectedCategory) params.category = selectedCategory;
      if (searchTerm) params.search = searchTerm;

      const response = await axios.get(
        `${API_BASE_URL}/api/clientes/${clienteId}/documents`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params
        }
      );

      setDocuments(response.data.data.documents);
      setCategories(response.data.data.categories);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast({
        title: 'Erro ao carregar documentos',
        description: error.response?.data?.message || 'Erro ao carregar documentos',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, [clienteId, selectedCategory, searchTerm]);

  // Get file icon based on type
  const getFileIcon = (documentType) => {
    switch (documentType) {
      case 'pdf':
        return FaFilePdf;
      case 'doc':
      case 'docx':
        return FaFileWord;
      default:
        return FaFileAlt;
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Handle file upload
  const handleFileUpload = async () => {
    if (!uploadForm.file || !uploadForm.category || !uploadForm.description) {
      toast({
        title: 'Campos obrigatórios',
        description: 'Por favor, preencha todos os campos',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const formData = new FormData();
    formData.append('file', uploadForm.file);
    formData.append('category', uploadForm.category);
    formData.append('description', uploadForm.description);

    try {
      setIsUploading(true);
      const token = localStorage.getItem('token');
      
      await axios.post(
        `${API_BASE_URL}/api/clientes/${clienteId}/documents/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(percentCompleted);
          }
        }
      );

      toast({
        title: 'Documento enviado',
        description: 'Documento enviado com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      setShowUploadModal(false);
      setUploadForm({ file: null, category: '', description: '' });
      setUploadProgress(0);
      fetchDocuments();
    } catch (error) {
      console.error('Error uploading document:', error);
      toast({
        title: 'Erro ao enviar documento',
        description: error.response?.data?.message || 'Erro ao enviar documento',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsUploading(false);
    }
  };

  // Handle document deletion
  const handleDeleteDocument = async (documentId, deleteAllVersions = false) => {
    if (!window.confirm(
      deleteAllVersions 
        ? 'Tem certeza que deseja excluir todas as versões deste documento?' 
        : 'Tem certeza que deseja excluir este documento?'
    )) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_BASE_URL}/api/clientes/${clienteId}/documents/${documentId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { deleteAllVersions }
        }
      );

      toast({
        title: 'Documento excluído',
        description: 'Documento excluído com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      fetchDocuments();
    } catch (error) {
      console.error('Error deleting document:', error);
      toast({
        title: 'Erro ao excluir documento',
        description: error.response?.data?.message || 'Erro ao excluir documento',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Handle document download
  const handleDownloadDocument = async (documentId, documentName) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/api/clientes/${clienteId}/documents/${documentId}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', documentName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading document:', error);
      toast({
        title: 'Erro ao baixar documento',
        description: 'Erro ao baixar documento',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Box p={6}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <HStack justify="space-between" wrap="wrap" spacing={4}>
          <Text fontSize="2xl" fontWeight="bold" color={useColorModeValue('gray.800', 'white')}>
            Documentos
          </Text>
          
          <HStack spacing={3}>
            {canModify && (
              <>
                <Button
                  leftIcon={<FaFolder />}
                  size="sm"
                  variant="outline"
                  onClick={() => setShowCategoryModal(true)}
                >
                  Categorias
                </Button>
                <Button
                  leftIcon={<FaUpload />}
                  colorScheme="green"
                  size="sm"
                  onClick={() => setShowUploadModal(true)}
                >
                  Enviar Documento
                </Button>
              </>
            )}
          </HStack>
        </HStack>

        {/* Filters */}
        <HStack spacing={4} wrap="wrap">
          <InputGroup maxW="300px">
            <InputLeftElement pointerEvents="none">
              <FaSearch color="gray.300" />
            </InputLeftElement>
            <Input
              placeholder="Buscar documentos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>

          <Select
            placeholder="Todas as categorias"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            maxW="200px"
          >
            {categories.map((cat) => (
              <option key={cat._id} value={cat.name}>
                {cat.name} ({cat.documentCount})
              </option>
            ))}
          </Select>
        </HStack>

        {/* Documents Table */}
        <Box
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="lg"
          overflow="hidden"
        >
          {loading ? (
            <Center p={10}>
              <Spinner size="lg" color="green.500" />
            </Center>
          ) : documents.length === 0 ? (
            <Center p={10}>
              <VStack>
                <Icon as={HiOutlineDocumentText} boxSize={16} color="gray.300" />
                <Text color="gray.500">Nenhum documento encontrado</Text>
              </VStack>
            </Center>
          ) : (
            <Table variant="simple">
              <Thead bg={bgColor}>
                <Tr>
                  <Th>Tipo</Th>
                  <Th>Nome</Th>
                  <Th>Descrição</Th>
                  <Th>Categoria</Th>
                  <Th>Tamanho</Th>
                  <Th>Enviado por</Th>
                  <Th>Data</Th>
                  <Th>Ações</Th>
                </Tr>
              </Thead>
              <Tbody>
                {documents.map((doc) => (
                  <Tr key={doc._id} _hover={{ bg: hoverBg }}>
                    <Td>
                      <Icon
                        as={getFileIcon(doc.documentType)}
                        boxSize={5}
                        color={doc.documentType === 'pdf' ? 'red.500' : 'blue.500'}
                      />
                    </Td>
                    <Td>
                      <VStack align="start" spacing={0}>
                        <Text fontWeight="medium">{doc.originalName}</Text>
                        {doc.version > 1 && (
                          <Badge size="sm" colorScheme="blue">
                            v{doc.version}
                          </Badge>
                        )}
                      </VStack>
                    </Td>
                    <Td>
                      <Text noOfLines={2}>{doc.description}</Text>
                    </Td>
                    <Td>
                      <Badge colorScheme="purple">{doc.category}</Badge>
                    </Td>
                    <Td>{formatFileSize(doc.size)}</Td>
                    <Td>{doc.uploadedBy?.nomeCompleto || 'N/A'}</Td>
                    <Td>{new Date(doc.createdAt).toLocaleDateString('pt-BR')}</Td>
                    <Td>
                      <HStack spacing={2}>
                        <Tooltip label="Visualizar">
                          <IconButton
                            size="sm"
                            icon={<FaEye />}
                            variant="ghost"
                            onClick={() => {
                              setSelectedDocument(doc);
                              setShowPreviewModal(true);
                            }}
                          />
                        </Tooltip>
                        <Tooltip label="Baixar">
                          <IconButton
                            size="sm"
                            icon={<FaDownload />}
                            variant="ghost"
                            onClick={() => handleDownloadDocument(doc._id, doc.originalName)}
                          />
                        </Tooltip>
                        
                        <Menu>
                          <MenuButton
                            as={IconButton}
                            icon={<FaEllipsisV />}
                            variant="ghost"
                            size="sm"
                          />
                          <MenuList>
                            <MenuItem
                              icon={<FaHistory />}
                              onClick={() => {
                                setSelectedDocument(doc);
                                setShowVersionModal(true);
                              }}
                            >
                              Histórico de Versões
                            </MenuItem>
                            {canModify && (
                              <>
                                <Divider />
                                <MenuItem
                                  icon={<FaTrash />}
                                  color="red.500"
                                  onClick={() => handleDeleteDocument(doc._id)}
                                >
                                  Excluir esta versão
                                </MenuItem>
                                <MenuItem
                                  icon={<FaTrash />}
                                  color="red.600"
                                  onClick={() => handleDeleteDocument(doc._id, true)}
                                >
                                  Excluir todas as versões
                                </MenuItem>
                              </>
                            )}
                          </MenuList>
                        </Menu>
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </Box>
      </VStack>

      {/* Upload Modal */}
      <Modal
        isOpen={showUploadModal}
        onClose={() => {
          setShowUploadModal(false);
          setUploadForm({ file: null, category: '', description: '' });
          setUploadProgress(0);
        }}
        size="md"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Enviar Documento</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Arquivo</FormLabel>
                <Input
                  type="file"
                  accept=".pdf,.doc,.docx,.txt,.rtf,.odt"
                  onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files[0] })}
                  disabled={isUploading}
                />
                <Text fontSize="sm" color="gray.500" mt={1}>
                  Tipos aceitos: PDF, Word, TXT. Máx: 20MB
                </Text>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Categoria</FormLabel>
                <Select
                  placeholder="Selecione uma categoria"
                  value={uploadForm.category}
                  onChange={(e) => setUploadForm({ ...uploadForm, category: e.target.value })}
                  disabled={isUploading}
                >
                  {categories.map((cat) => (
                    <option key={cat._id} value={cat.name}>
                      {cat.name}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Descrição</FormLabel>
                <Textarea
                  placeholder="Descreva o documento..."
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                  disabled={isUploading}
                />
              </FormControl>

              {isUploading && (
                <Box w="100%">
                  <Text fontSize="sm" mb={2}>Enviando...</Text>
                  <Progress value={uploadProgress} colorScheme="green" />
                </Box>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => setShowUploadModal(false)} disabled={isUploading}>
              Cancelar
            </Button>
            <Button
              colorScheme="green"
              onClick={handleFileUpload}
              isLoading={isUploading}
              loadingText="Enviando..."
              disabled={!uploadForm.file || !uploadForm.category || !uploadForm.description}
            >
              Enviar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Category Manager Modal */}
      {showCategoryModal && (
        <CategoryManager
          isOpen={showCategoryModal}
          onClose={() => setShowCategoryModal(false)}
          clienteId={clienteId}
          onCategoryChange={fetchDocuments}
        />
      )}

      {/* Document Preview Modal */}
      {showPreviewModal && selectedDocument && (
        <DocumentPreview
          isOpen={showPreviewModal}
          onClose={() => setShowPreviewModal(false)}
          document={selectedDocument}
          clienteId={clienteId}
        />
      )}

      {/* Version History Modal */}
      {showVersionModal && selectedDocument && (
        <VersionHistory
          isOpen={showVersionModal}
          onClose={() => setShowVersionModal(false)}
          document={selectedDocument}
          clienteId={clienteId}
          onVersionChange={fetchDocuments}
        />
      )}
    </Box>
  );
};

export default Documentos;
